import { ChatMessage, findResyncSince } from './mergeMessages';

/** 補缺口的分頁上限。缺口再大也不該讓 widget 一直拉下去。 */
export const RESYNC_PAGE_LIMIT = 50;
export const RESYNC_MAX_PAGES = 10;

type FetchSince = (since: string, limit: number) => Promise<{ results?: ChatMessage[] } | undefined>;

export interface ResyncDeps {
	/** 目前列表（要拿最新值，不要拿 render 當時的閉包快照）。 */
	getMessages: () => ChatMessage[];
	fetchSince: FetchSince;
	/** 補拉到的訊息，交給呼叫方併進列表。 */
	onMerged: (fetched: ChatMessage[]) => void;
	/** 補拉整段成功跑完才呼叫，用來對帳本地 pending。 */
	onSettled: () => void;
	onError: (error: unknown) => void;
}

/**
 * 從 `since` 開始一路把缺口拉完，回傳所有拉到的訊息（由舊往新）。
 *
 * 抽成純函數是為了能單獨測分頁與續拉的推進條件 —— 這段最容易寫出「拿滿一頁就停」或
 * 「基準沒推進導致無限迴圈」這兩種錯。
 */
export async function fetchGapSince(fetchSince: FetchSince, since: string): Promise<ChatMessage[]> {
	const collected: ChatMessage[] = [];
	let cursor = since;

	for (let page = 0; page < RESYNC_MAX_PAGES; page += 1) {
		// 分頁必須序列跑：下一頁的基準要用這一頁最新的那筆時間，沒辦法並行。
		// eslint-disable-next-line no-await-in-loop
		const response = await fetchSince(cursor, RESYNC_PAGE_LIMIT);
		const fetched = response?.results || [];
		if (fetched.length === 0) {
			break;
		}

		collected.push(...fetched);

		// 沒拿滿一頁就是拿完了。
		if (fetched.length < RESYNC_PAGE_LIMIT) {
			break;
		}
		// 拿滿了才把基準推到這批最新的一筆再續拉；推不動就停，否則會原地打轉。
		const nextCursor = findResyncSince(fetched);
		if (!nextCursor || nextCursor === cursor) {
			break;
		}
		cursor = nextCursor;
	}

	return collected;
}

/**
 * 建立一支「重連後補拉」的函數，內含同時只跑一次的守衛。
 *
 * socket.io 在網路不穩時會連續丟 `reconnect`，不擋就會疊出好幾個同樣的請求。守衛在成功與失敗
 * 兩條路都會釋放，所以後續的 reconnect 還能再補一次。
 */
export function createResync(deps: ResyncDeps): () => Promise<void> {
	let inFlight = false;

	return async function resync(): Promise<void> {
		if (inFlight) {
			return;
		}
		// 基準只取 server 認過（有 objectId）的訊息；還沒有的話就交給掛載時的初次載入。
		const since = findResyncSince(deps.getMessages());
		if (!since) {
			return;
		}

		inFlight = true;
		try {
			const fetched = await fetchGapSince(deps.fetchSince, since);
			if (fetched.length > 0) {
				deps.onMerged(fetched);
			}
			// 只在整段補拉跑完才對帳 pending：中途失敗就把顧客的訊息標成失敗會是誤判。
			deps.onSettled();
		} catch (error) {
			deps.onError(error);
		} finally {
			inFlight = false;
		}
	};
}
